import React, { useEffect, useMemo, useState } from 'react';
import { BELONGS_KEYS, BELONGS_META } from './config/belongs';
import { parsePaymentMethod } from './utils/cells';
import {
    LICENSE_PERMIT_TYPES,
    LICENSE_TEMPLATES,
} from './config/trackers';
import InlineDatePicker from '../global/InlineDatePicker';

/**
 * Field-driven create/edit form.
 *
 * `tracker.fields` is the SINGLE source of truth — we iterate it directly
 * for every input including label/address/paymentMethod. Auto-pay day is
 * combined with paymentMethod into the legacy "Auto-pay · day {N}" string
 * before save (and parsed apart on edit).
 *
 * Tenant Rent locks belongs=property per spec § 12.
 * Mortgage shows the auto-amortization info-box only when type === 'Mortgage'.
 */
function AddAccountModal({ tracker, initial, onClose, onSave, onSaveMany }) {
    // Custom multi-permit Renewals modal — only on add (edit falls through to
    // the standard single-record form so users can tweak existing licenses).
    if (tracker.key === 'licenses' && !initial) {
        return (
            <LicensesAddModal
                tracker={tracker}
                onClose={onClose}
                onSave={onSave}
                onSaveMany={onSaveMany}
            />
        );
    }
    return (
        <StandardAddAccountModal
            tracker={tracker}
            initial={initial}
            onClose={onClose}
            onSave={onSave}
            onSaveMany={onSaveMany}
        />
    );
}

function StandardAddAccountModal({ tracker, initial, onClose, onSave, onSaveMany }) {
    const isEdit = !!initial;
    const isLocked = tracker.lockedBelongs === 'property';

    const [belongs, setBelongs] = useState(
        isLocked ? 'property' : initial?.belongs || 'gameroom',
    );

    // Seed values: parse paymentMethod string into method + day so the two
    // form inputs reflect the saved state.
    const [fieldVals, setFieldVals] = useState(() => {
        const seed = initial?.fields || {};
        const out = {};
        (tracker.fields || []).forEach((f) => {
            out[f.name] = seed[f.name] != null ? String(seed[f.name]) : '';
        });
        // Backfill label from row.label (since the field is named 'label' but
        // also persisted at the row level).
        if (initial?.label) out.label = initial.label;
        // Parse paymentMethod string into the two inputs if present.
        if (initial?.paymentMethod) {
            const parsed = parsePaymentMethod(initial.paymentMethod);
            if (parsed.isAuto) {
                out.paymentMethod = 'Auto-pay';
                out.autoPayDay = parsed.day ? String(parsed.day) : '';
            } else if (out.paymentMethod === '' || !out.paymentMethod) {
                out.paymentMethod = parsed.method || 'Manual';
            }
        }
        return out;
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isLocked) setBelongs('property');
    }, [isLocked]);

    // Fields list excluding the special label field, which we render in its own row first.
    const detailFields = useMemo(
        () => (tracker.fields || []).filter((f) => f.name !== 'label'),
        [tracker.fields],
    );

    const requiredFields = useMemo(
        () => (tracker.fields || []).filter((f) => f.required),
        [tracker.fields],
    );

    // Should we show field f? Mortgage-only sub-fields hide for type=Rent, etc.
    const isFieldVisible = (f) => {
        if (tracker.key === 'mortgage') {
            const isMortgage = fieldVals.mortgageType === 'Mortgage';
            if (['originalLoanAmount', 'interestRate', 'loanTerm'].includes(f.name)) {
                return isMortgage;
            }
            if (f.name === 'leaseEndDate') {
                return fieldVals.mortgageType === 'Rent';
            }
        }
        if (tracker.key === 'propertyTax') {
            if (f.name === 'mudName' && fieldVals.hasMud !== 'Yes') return false;
        }
        if (f.name === 'autoPayDay' && fieldVals.paymentMethod !== 'Auto-pay') {
            return false;
        }
        return true;
    };

    const handleSave = () => {
        const next = {};
        const labelVal = (fieldVals.label || '').trim();
        if (!labelVal) next.label = 'Required';
        requiredFields.forEach((f) => {
            if (f.name === 'label') return;
            if (!isFieldVisible(f)) return;
            const v = fieldVals[f.name];
            if (!v || !String(v).trim()) next[f.name] = 'Required';
        });
        setErrors(next);
        if (Object.keys(next).length > 0) return;

        // Compose the legacy paymentMethod string the cells codec expects.
        let composedPaymentMethod = fieldVals.paymentMethod || 'Manual';
        if (composedPaymentMethod === 'Auto-pay' && fieldVals.autoPayDay) {
            composedPaymentMethod = `Auto-pay · day ${fieldVals.autoPayDay}`;
        } else if (composedPaymentMethod === 'Auto-pay (escrow)') {
            // Property-tax style → keep as-is for the codec
            composedPaymentMethod = 'Auto-pay (escrow)';
        }

        // Build cleaned field map — strip the synthetic paymentMethod/autoPayDay
        // (those go on the row as paymentMethod) and any blank values.
        const cleanedFields = {};
        Object.keys(fieldVals).forEach((k) => {
            if (k === 'label' || k === 'paymentMethod' || k === 'autoPayDay') return;
            const v = fieldVals[k];
            if (v !== '' && v != null) cleanedFields[k] = v;
        });

        // Maintenance-specific defaults: stamp dateRequested + status on
        // create. Vendor / costs are filled in later via the order detail
        // popup, so we never block the user on those fields.
        if (tracker.key === 'maintenance' && !isEdit) {
            const today = new Date();
            const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            cleanedFields.dateRequested = cleanedFields.dateRequested || iso;
            cleanedFields.orderStatus = cleanedFields.orderStatus || 'Requested';
            if (!cleanedFields.vendor) cleanedFields.vendor = 'TBD';
            cleanedFields.quotes = cleanedFields.quotes || [];
            cleanedFields.photos = cleanedFields.photos || [];
            cleanedFields.updates = cleanedFields.updates || [{
                icon: '📋',
                type: 'Order created',
                date: today.toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                }),
                text: `Order requested${cleanedFields.priority ? ` · ${cleanedFields.priority}` : ''}`,
            }];
        }

        const finalBelongs = tracker.key === 'maintenance'
            ? (initial?.belongs || belongs || 'gameroom')
            : belongs;

        const payload = {
            accountId: initial?.accountId,
            trackerKey: tracker.key,
            label: labelVal,
            belongs: finalBelongs,
            frequency:
                tracker.key === 'salesTax' && cleanedFields.filingFrequency
                    ? cleanedFields.filingFrequency.toLowerCase()
                    : tracker.frequency,
            fields: cleanedFields,
            paymentMethod: composedPaymentMethod,
            subTaxLabels: tracker.subTaxLabels,
            // Preserve existing months/subTaxes on edit.
            months: initial?.months,
            subTaxes: initial?.subTaxes,
        };
        onSave(payload);
    };

    const setVal = (name, v) => {
        setFieldVals((prev) => ({ ...prev, [name]: v }));
    };

    const labelField = (tracker.fields || []).find((f) => f.name === 'label')
        || { name: 'label', label: 'Location Name', type: 'text', required: true };

    return (
        <div className="bt-overlay" onClick={onClose}>
            <div
                className="bt-modal"
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bt-modal-head">
                    <h2>
                        {tracker.addModalTitle && !isEdit
                            ? tracker.addModalTitle
                            : `${isEdit ? 'Edit' : 'Add'} ${tracker.label} ${tracker.label === 'Tenant Rent' ? 'Tenant' : 'Account'}`}
                    </h2>
                    <button
                        className="bt-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                        type="button"
                    >
                        ✕
                    </button>
                </div>

                {/* Belongs picker — skipped for trackers that derive belongs from a field. */}
                {tracker.key !== 'maintenance' && (
                    <div className="bt-modal-section">
                        <div className="bt-modal-section-label">Belongs To *</div>
                        <BelongsPicker
                            value={belongs}
                            onChange={setBelongs}
                            locked={isLocked}
                        />
                    </div>
                )}

                {/* Account details */}
                <div className="bt-modal-section">
                    <div className="bt-modal-section-label">Account Details</div>

                    <FieldRow
                        field={labelField}
                        value={fieldVals.label || ''}
                        error={errors.label}
                        onChange={(v) => setVal('label', v)}
                    />

                    {detailFields.filter(isFieldVisible).map((f) => (
                        <FieldRow
                            key={f.name}
                            field={f}
                            value={fieldVals[f.name] || ''}
                            error={errors[f.name]}
                            onChange={(v) => setVal(f.name, v)}
                        />
                    ))}
                </div>

                {/* Mortgage info-box: auto-amortization explainer when type=Mortgage */}
                {tracker.key === 'mortgage' && fieldVals.mortgageType === 'Mortgage' && (
                    <div className="bt-info-box">
                        <strong>
                            <span role="img" aria-label="Info">ℹ️</span> Auto-amortization enabled.
                        </strong>{' '}
                        Each monthly payment is split between principal and interest based on the rate, term, and original loan amount above. The remaining balance updates automatically as payments are recorded.
                    </div>
                )}

                {/* Universal status info-box */}
                <div className="bt-info-box">
                    <strong>
                        <span role="img" aria-label="Info">ℹ️</span> Status is managed automatically.
                    </strong>{' '}
                    New accounts start as Active and update based on contract / expiration dates.
                </div>

                <div className="bt-modal-actions">
                    <button className="bt-btn bt-btn-ghost" onClick={onClose} type="button">
                        Cancel
                    </button>
                    <button className="bt-btn bt-btn-save" onClick={handleSave} type="button">
                        {isEdit
                            ? 'Save Changes'
                            : tracker.key === 'maintenance'
                                ? 'Create Order'
                                : 'Save Account'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function BelongsPicker({ value, onChange, locked }) {
    if (locked) {
        // Single locked Property card spanning the row.
        const meta = BELONGS_META.property;
        return (
            <div className="bt-belongs-cards">
                <div className="bt-belongs-card-pick sel locked">
                    <span className="bt-belongs-card-pick-icon" role="img" aria-label={meta.label}>
                        {meta.icon}
                    </span>
                    {meta.label}
                    <div className="bt-belongs-card-pick-hint">
                        Tenant Rent always belongs to Property Management.
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="bt-belongs-cards">
            {BELONGS_KEYS.map((k) => {
                const meta = BELONGS_META[k];
                const active = value === k;
                return (
                    <div
                        key={k}
                        className={`bt-belongs-card-pick${active ? ' sel' : ''}`}
                        onClick={() => onChange(k)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && onChange(k)}
                    >
                        <span className="bt-belongs-card-pick-icon" role="img" aria-label={meta.label}>
                            {meta.icon}
                        </span>
                        {meta.label}
                    </div>
                );
            })}
        </div>
    );
}

function FieldRow({ field, value, error, onChange }) {
    const id = `bt-fld-${field.name}`;
    return (
        <div className="bt-field">
            <label className="bt-field-label" htmlFor={id}>
                {field.label}{field.required && <span className="req"> *</span>}
            </label>
            {field.type === 'select' ? (
                <select
                    id={id}
                    className="bt-field-select"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                >
                    <option value="">— select —</option>
                    {(field.options || []).map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            ) : field.type === 'date' ? (
                <InlineDatePicker
                    value={value || ''}
                    onChange={onChange}
                    placeholder={field.placeholder || 'Select date'}
                />
            ) : (
                <input
                    id={id}
                    type={field.type === 'number' ? 'number' : 'text'}
                    className="bt-field-input"
                    value={value || ''}
                    placeholder={field.placeholder || ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            )}
            {error && <div className="bt-field-error">{error}</div>}
        </div>
    );
}

/**
 * Licenses & Permits — multi-permit builder modal (spec § 16).
 *
 * The user picks a business-type template (or "Custom"), then sees a
 * checkbox for each of the 10 recurring permits with the template's
 * defaults pre-checked. For each checked permit, an inline mini-form
 * collects authority, permit#, issued/expires, cost, and frequency.
 *
 * On save, fan out to onSaveMany with one payload per checked permit
 * (location is shared across them — multiple permits, one location).
 */
function LicensesAddModal({ tracker, onClose, onSave, onSaveMany }) {
    const [belongs, setBelongs] = useState('gameroom');
    const [locationName, setLocationName] = useState('');
    const [address, setAddress] = useState('');
    const [templateKey, setTemplateKey] = useState('custom');
    const [checked, setChecked] = useState({});
    // Per-permit inline form state, keyed by permit id.
    const [permitForms, setPermitForms] = useState({});
    const [errors, setErrors] = useState({});

    const applyTemplate = (key) => {
        setTemplateKey(key);
        const tmpl = LICENSE_TEMPLATES[key];
        if (!tmpl) return;
        const next = {};
        tmpl.permits.forEach((id) => { next[id] = true; });
        setChecked(next);
        // Seed inline forms with the permit defaults so the user only has to
        // fill the auth + permit# + dates.
        const seedForms = { ...permitForms };
        tmpl.permits.forEach((id) => {
            if (!seedForms[id]) {
                const meta = LICENSE_PERMIT_TYPES.find((p) => p.id === id);
                if (meta) {
                    seedForms[id] = {
                        authority: meta.authority,
                        permitNumber: '',
                        issued: '',
                        expires: '',
                        renewalCost: meta.cost,
                        frequency: meta.freq,
                    };
                }
            }
        });
        setPermitForms(seedForms);
    };

    const togglePermit = (id, on) => {
        setChecked((prev) => ({ ...prev, [id]: on }));
        if (on && !permitForms[id]) {
            const meta = LICENSE_PERMIT_TYPES.find((p) => p.id === id);
            if (meta) {
                setPermitForms((prev) => ({
                    ...prev,
                    [id]: {
                        authority: meta.authority,
                        permitNumber: '',
                        issued: '',
                        expires: '',
                        renewalCost: meta.cost,
                        frequency: meta.freq,
                    },
                }));
            }
        }
    };

    const updatePermitField = (id, key, value) => {
        setPermitForms((prev) => ({
            ...prev,
            [id]: { ...(prev[id] || {}), [key]: value },
        }));
    };

    const handleSave = () => {
        const next = {};
        if (!locationName.trim()) next.locationName = 'Required';
        if (!address.trim()) next.address = 'Required';
        const checkedIds = Object.keys(checked).filter((k) => checked[k]);
        if (checkedIds.length === 0) next.permits = 'Pick at least one permit';
        setErrors(next);
        if (Object.keys(next).length > 0) return;

        const payloads = checkedIds.map((id) => {
            const meta = LICENSE_PERMIT_TYPES.find((p) => p.id === id);
            const f = permitForms[id] || {};
            return {
                trackerKey: 'licenses',
                label: locationName.trim(),
                belongs,
                frequency: 'asNeeded',
                fields: {
                    address: address.trim(),
                    permitTypeId: id,
                    permitType: meta?.label || id,
                    authority: f.authority || meta?.authority || '',
                    permitNumber: f.permitNumber || '',
                    issued: f.issued || '',
                    expires: f.expires || '',
                    renewalCost: f.renewalCost || meta?.cost || '',
                    frequency: f.frequency || meta?.freq || 'Annual',
                },
                paymentMethod: 'Manual',
            };
        });

        if (onSaveMany) {
            onSaveMany(payloads);
        } else {
            // Fallback: parent only wired onSave — fire them sequentially.
            payloads.forEach((p) => onSave(p));
        }
    };

    const checkedCount = Object.keys(checked).filter((k) => checked[k]).length;

    return (
        <div className="bt-overlay" onClick={onClose}>
            <div
                className="bt-modal"
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bt-modal-head">
                    <h2>Add Renewal</h2>
                    <button
                        className="bt-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                        type="button"
                    >
                        ✕
                    </button>
                </div>

                <div className="bt-modal-section">
                    <div className="bt-modal-section-label">Belongs To *</div>
                    <BelongsPicker value={belongs} onChange={setBelongs} locked={false} />
                </div>

                <div className="bt-modal-section">
                    <div className="bt-modal-section-label">Location</div>
                    <div className="bt-field">
                        <label className="bt-field-label">
                            Location Name <span className="req">*</span>
                        </label>
                        <input
                            type="text"
                            className="bt-field-input"
                            value={locationName}
                            onChange={(e) => setLocationName(e.target.value)}
                            placeholder="e.g. Fuego 777"
                        />
                        {errors.locationName && (
                            <div className="bt-field-error">{errors.locationName}</div>
                        )}
                    </div>
                    <div className="bt-field">
                        <label className="bt-field-label">
                            Address <span className="req">*</span>
                        </label>
                        <input
                            type="text"
                            className="bt-field-input"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="e.g. 1234 Mason Rd, Katy, TX"
                        />
                        {errors.address && (
                            <div className="bt-field-error">{errors.address}</div>
                        )}
                    </div>
                </div>

                <div className="bt-modal-section">
                    <div className="bt-modal-section-label">Business Template</div>
                    <select
                        className="bt-field-select"
                        value={templateKey}
                        onChange={(e) => applyTemplate(e.target.value)}
                    >
                        {Object.keys(LICENSE_TEMPLATES).map((k) => (
                            <option key={k} value={k}>{LICENSE_TEMPLATES[k].label}</option>
                        ))}
                    </select>
                    <div className="bt-field-hint">
                        Pick a template to pre-check the typical permits for this business
                        type. You can still toggle individual permits below.
                    </div>
                </div>

                <div className="bt-modal-section">
                    <div className="bt-modal-section-label">
                        Permits ({checkedCount} selected)
                    </div>
                    {errors.permits && (
                        <div className="bt-field-error" style={{ marginBottom: 8 }}>
                            {errors.permits}
                        </div>
                    )}
                    <div className="bt-permit-builder">
                        {LICENSE_PERMIT_TYPES.map((p) => {
                            const isChecked = !!checked[p.id];
                            const form = permitForms[p.id] || {};
                            return (
                                <div
                                    key={p.id}
                                    className={`bt-permit-builder-row${isChecked ? ' checked' : ''}`}
                                >
                                    <label className="bt-permit-builder-head">
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={(e) => togglePermit(p.id, e.target.checked)}
                                        />
                                        <span className="bt-permit-builder-icon" role="img" aria-label={p.label}>
                                            {p.icon}
                                        </span>
                                        <span className="bt-permit-builder-name">{p.label}</span>
                                        <span className="bt-permit-builder-cost">
                                            {p.cost} · {p.freq}
                                        </span>
                                    </label>
                                    {isChecked && (
                                        <div className="bt-permit-builder-form">
                                            <div className="bt-permit-builder-grid">
                                                <div className="bt-field">
                                                    <label className="bt-field-label">Issuing Authority</label>
                                                    <input
                                                        type="text"
                                                        className="bt-field-input"
                                                        value={form.authority || ''}
                                                        onChange={(e) => updatePermitField(p.id, 'authority', e.target.value)}
                                                    />
                                                </div>
                                                <div className="bt-field">
                                                    <label className="bt-field-label">Permit #</label>
                                                    <input
                                                        type="text"
                                                        className="bt-field-input"
                                                        value={form.permitNumber || ''}
                                                        onChange={(e) => updatePermitField(p.id, 'permitNumber', e.target.value)}
                                                        placeholder="e.g. BL-2025-1234"
                                                    />
                                                </div>
                                                <div className="bt-field">
                                                    <label className="bt-field-label">Issued</label>
                                                    <InlineDatePicker
                                                        value={form.issued || ''}
                                                        onChange={(v) => updatePermitField(p.id, 'issued', v)}
                                                        placeholder="Issued date"
                                                    />
                                                </div>
                                                <div className="bt-field">
                                                    <label className="bt-field-label">Expires</label>
                                                    <InlineDatePicker
                                                        value={form.expires || ''}
                                                        onChange={(v) => updatePermitField(p.id, 'expires', v)}
                                                        placeholder="Expiration date"
                                                    />
                                                </div>
                                                <div className="bt-field">
                                                    <label className="bt-field-label">Renewal Cost</label>
                                                    <input
                                                        type="text"
                                                        className="bt-field-input"
                                                        value={form.renewalCost || ''}
                                                        onChange={(e) => updatePermitField(p.id, 'renewalCost', e.target.value)}
                                                    />
                                                </div>
                                                <div className="bt-field">
                                                    <label className="bt-field-label">Frequency</label>
                                                    <select
                                                        className="bt-field-select"
                                                        value={form.frequency || 'Annual'}
                                                        onChange={(e) => updatePermitField(p.id, 'frequency', e.target.value)}
                                                    >
                                                        <option>Annual</option>
                                                        <option>Biennial (2 yrs)</option>
                                                        <option>Triennial (3 yrs)</option>
                                                        <option>5-year</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="bt-modal-actions">
                    <button className="bt-btn bt-btn-ghost" onClick={onClose} type="button">
                        Cancel
                    </button>
                    <button className="bt-btn bt-btn-save" onClick={handleSave} type="button">
                        Save {checkedCount > 0 ? `${checkedCount} ` : ''}Permit{checkedCount === 1 ? '' : 's'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AddAccountModal;
